import { useState, useMemo } from 'react';
import JSZip from 'jszip';
import { Download, FileText } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';

const TOKENS: Array<{ token: string; description: string }> = [
  { token: '{name}', description: 'Original filename (no extension)' },
  { token: '{ext}', description: 'Original extension' },
  { token: '{i}', description: 'Index, 1, 2, 3…' },
  { token: '{i:3}', description: 'Padded index, 001, 002, 003…' },
  { token: '{date}', description: 'Today, YYYY-MM-DD' },
  { token: '{w}', description: 'Image width' },
  { token: '{h}', description: 'Image height' },
];

interface PreviewRow {
  src: string;
  out: string;
  file: File;
  width?: number;
  height?: number;
}

async function imageDimensions(file: File): Promise<{ w: number; h: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      resolve({ w: img.width, h: img.height });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve({ w: 0, h: 0 });
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

function formatToken(pattern: string, ctx: { name: string; ext: string; i: number; w: number; h: number }): string {
  const today = new Date().toISOString().slice(0, 10);
  return pattern
    .replace(/\{name\}/g, ctx.name)
    .replace(/\{ext\}/g, ctx.ext)
    .replace(/\{i:(\d+)\}/g, (_, n) => String(ctx.i).padStart(parseInt(n, 10), '0'))
    .replace(/\{i\}/g, String(ctx.i))
    .replace(/\{date\}/g, today)
    .replace(/\{w\}/g, String(ctx.w))
    .replace(/\{h\}/g, String(ctx.h));
}

export function ImageBulkRename() {
  const [files, setFiles] = useState<File[]>([]);
  const [pattern, setPattern] = useState('{name}-{w}x{h}');
  const [meta, setMeta] = useState<Record<string, { w: number; h: number }>>({});
  const [working, setWorking] = useState(false);

  async function handleFiles(newFiles: File[]) {
    setFiles((p) => [...p, ...newFiles]);
    for (const f of newFiles) {
      if (!f.type.startsWith('image/')) continue;
      const dim = await imageDimensions(f);
      setMeta((m) => ({ ...m, [f.name + f.size]: dim }));
    }
  }

  const preview = useMemo<PreviewRow[]>(() => {
    return files.map((f, i) => {
      const lastDot = f.name.lastIndexOf('.');
      const name = lastDot > 0 ? f.name.slice(0, lastDot) : f.name;
      const ext = lastDot > 0 ? f.name.slice(lastDot + 1) : '';
      const dim = meta[f.name + f.size] ?? { w: 0, h: 0 };
      const computed = formatToken(pattern, { name, ext, i: i + 1, w: dim.w, h: dim.h });
      const out = computed.includes('.') ? computed : computed + (ext ? '.' + ext : '');
      return { src: f.name, out, file: f, ...dim };
    });
  }, [files, pattern, meta]);

  async function exportZip() {
    if (!preview.length) return;
    setWorking(true);
    try {
      const zip = new JSZip();
      const used = new Set<string>();
      for (const row of preview) {
        let name = row.out;
        let i = 1;
        while (used.has(name)) {
          const lastDot = row.out.lastIndexOf('.');
          name = lastDot > 0 ? `${row.out.slice(0, lastDot)} (${i})${row.out.slice(lastDot)}` : `${row.out} (${i})`;
          i++;
        }
        used.add(name);
        zip.file(name, await row.file.arrayBuffer());
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      saveAs(blob, 'renamed.zip');
      toast.success(`Renamed ${preview.length} files`);
    } finally {
      setWorking(false);
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="image-bulk-rename"
        title="Bulk image rename"
        description="Rename hundreds of files with a pattern. Outputs a clean ZIP."
        badges={files.length ? <Badge>{files.length} files</Badge> : null}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <Card>
            <Dropzone
              accept="image/*"
              multiple
              onFiles={handleFiles}
              hint="Add as many image files as you like"
            />
          </Card>
          {preview.length > 0 && (
            <Card className="!p-0 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
                Preview
              </div>
              <div className="divide-y divide-border max-h-[440px] overflow-y-auto">
                {preview.map((p, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                    <code className="text-xs font-mono text-fg-subtle truncate flex-1">{p.src}</code>
                    <span className="text-fg-subtle">→</span>
                    <code className="text-xs font-mono font-medium text-accent truncate flex-1 text-right">
                      {p.out}
                    </code>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        <Card className="space-y-3">
          <Field label="Pattern">
            <Input
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              className="font-mono"
            />
          </Field>
          <div>
            <div className="text-2xs uppercase tracking-wider text-fg-subtle mb-1.5">Tokens</div>
            <div className="space-y-1">
              {TOKENS.map((t) => (
                <button
                  key={t.token}
                  onClick={() => setPattern((p) => p + t.token)}
                  className="w-full flex items-center justify-between px-2 py-1.5 rounded-md hover:bg-surface-hover text-left transition"
                >
                  <code className="text-2xs font-mono font-semibold">{t.token}</code>
                  <span className="text-2xs text-fg-subtle truncate ml-2">{t.description}</span>
                </button>
              ))}
            </div>
          </div>
          <Button
            variant="primary"
            className="w-full"
            disabled={!preview.length || working}
            loading={working}
            onClick={exportZip}
          >
            <Download size={11} />
            Export ZIP
          </Button>
        </Card>
      </div>
    </div>
  );
}

export default ImageBulkRename;
